import {useState} from "react";
import {Button} from "../../../../../components/general/input/Button";
import useCheckoutStore from "../../../../cart/store/checkoutStore";
import {paymentValidator} from "../../../../../components/payment/paymentValidator";
import useSession from "../../../../services/state/session/useSession";
import {useDispatch, useSelector} from "react-redux";
import {runInitialPaymentStep} from "../../../../../components/payment/PaymentProcessor";
import useValueChangeListener from "../../../../services/state/general/useValueChangeListener";
import {useShallow} from "zustand/react/shallow";
import {request} from "../../../../../res/rest/restRequest";
import {api} from "../../../../../res/rest/api";
import {env} from "../../../../../res/config/env";
import HttpClient from "../../../../services/http/HttpClient";
import {idempiereProcess} from "../../../../../res/rest/process";
import runAuthRequest from "../../../../../redux/util/runAuthRequest";

const addInputBlockers = (blockers, input) => {
    if(!(input.locationID > 0))
        blockers.push("Please select a delivery location")
    else if(!input.deliveryMethod)
        blockers.push('Please select a delivery method')
    else if(!input.deliveryDate)
        blockers.push("Please select a delivery date")
    else if(!input.paymentRule)
        blockers.push("Please select a payment type")
    else if(input.paymentRule === 'O') {
        if(!input.institutionId)
            blockers.push("Please select a bank")
    }
    else if(input.paymentRule === 'K') {
        const card = input.card;
        if(!card) {
            blockers.push("Please select a stored card, or enter a new one.")
        } else if((card.bankID ?? 0) <= 0) {
            if(!card.firstName || !card.lastName) {
                blockers.push("Full name on card is missing");
            } else if(paymentValidator.validateCardNumber(card.cardNumber)) {
                blockers.push("Card number is not valid");
            } else if(paymentValidator.validateExpiry(card.expireMonth + "/" + card.expireYear)) {
                blockers.push("Card expiry is not valid");
            } else if(paymentValidator.validateCvv(card.cvv)) {
                blockers.push("Card cvv not valid");
            }
        }
    }
}

const getCardPaymentInfo = (session, cardInput, amount, overPayment, charge_amount, locationID, orderID) => {
    return {
        ...cardInput,
        currency: 'GBP',
        overPayment,
        amount: amount.toFixed(2),
        locationID: locationID,
        bPartnerID: session.bPartnerID,
        userID: session.userID,
        orderID: orderID,
        surcharge: charge_amount
    };
}

const callCreatePaymentAuth = async (session, orderID, institutionId, callbackUrl) => {
    const req = request.process(api.PROCESS.YAPILY_CREATE_PAYMENT_AUTH)
        .param("C_Order_ID", orderID)
        .param("institutionId", institutionId)
        .param("callbackUrl", callbackUrl);
    const url = env.API_URL + req.buildRequest();
    const resp = await HttpClient.client.post(url, req.buildRequestBody(), api.auth(session));
    const data = idempiereProcess.handleResponseThrowingException(resp);
    return data.data.data;
};

const CartCheckoutButton = () => {

    const dispatch = useDispatch();
    const session = useSession();
    const blockers = [];
    const [openBankingLoading, setOpenBankingLoading] = useState(false);
    const [openBankingError, setOpenBankingError] = useState(null);
    const {
        orderID, locationID, input, prepareIt,
        completeIt, due, overPayment, setPaymentTransactionId
    } = useCheckoutStore(
        useShallow((state) => ({
            orderID: state.orderID,
            locationID: state.order?.C_BPartner_Location_ID?.id,
            input: state.input,
            prepareIt: state.prepareIt,
            completeIt: state.completeIt,
            due: state.getTotalDue(),
            overPayment: state.paymentDetails?.overPayment,
            setPaymentTransactionId: state.setPaymentTransactionId,
        }))
    );

    const { paymentResult } = useSelector(
        state => state.session.payment
    );
    useValueChangeListener(async () => {
        if(paymentResult && paymentResult.success) {
            completeIt(session);
        }
    }, [paymentResult]);

    addInputBlockers(blockers, input);
    const canCheckout = blockers.length === 0;

    const onClick = async () => {
        if(canCheckout) {
            if(input.paymentRule === 'O') {
                setOpenBankingLoading(true);
                setOpenBankingError(null);
                try {
                    const callbackUrl = window.location.origin + "/checkout";
                    const afterPrepare = async () => {
                        const { authorisationUrl, paymentTransactionId } = await runAuthRequest(
                            session.sessionData,
                            (s) => callCreatePaymentAuth(s, orderID, input.institutionId, callbackUrl),
                            session.dispatch
                        );
                        setPaymentTransactionId(paymentTransactionId);
                        window.location.href = authorisationUrl;
                    };
                    prepareIt(session, afterPrepare);
                } catch (e) {
                    const message = e?.message || "Failed to initiate Open Banking payment. Please try again.";
                    setOpenBankingError(message);
                    setOpenBankingLoading(false);
                }
            } else if(input.paymentRule === 'K') {
                const takePayment = () => runInitialPaymentStep(dispatch, session.sessionData, getCardPaymentInfo(
                    session.sessionData, input.card, due, overPayment ?? 0,
                    0, locationID, orderID
                ))
                prepareIt(session, takePayment);
            } else {
                prepareIt(session, () => completeIt(session));
            }
        }
    }

    if (openBankingLoading) {
        return (
            <>
                <p className="cart-blocker">Connecting to your bank...</p>
                <Button className="inactive">Checkout</Button>
            </>
        );
    }

    return  (
        <>
            {openBankingError &&
                <p className="cart-blocker">{openBankingError}</p>
            }
            {blockers.map((e,i) =>
                <p key={i} className="cart-blocker">{e}</p>
            )}
            <Button
                onClick={onClick}
                className={ canCheckout ? "" : "inactive" }
            >
                Checkout
            </Button>
        </>
    )
}

export default CartCheckoutButton;
